/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: utils/mappers.ts (Queues)
 🗓️ Created: 2025-01-15
 🧩 Description:
    Mapping utilities for converting between API responses and form state.

────────────────────────────────────────────────────────────────*/

import {
  FormState,
  QueueResponse,
  MappedFormState,
  TierResponse,
  TierRow,
  TimeBaseScore
} from '../types';
import { KNOWN_SETTINGS_KEYS } from '../constants';
import {
  extractExtraSettings,
  getStringSetting,
  getBooleanSetting,
  getNumberSetting
} from './settingsHelpers';
import { parseDestination } from './destinationHelpers';

/**
 * Map API response to form state
 */
export function mapResponseToState(queue: QueueResponse): MappedFormState {
  const settings =
    queue.settings && typeof queue.settings === 'object'
      ? (queue.settings as Record<string, unknown>)
      : {};

  const extraSettings = extractExtraSettings(settings);

  const asTimeBaseScore = (value: unknown, fallback: TimeBaseScore): TimeBaseScore => {
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      if (normalized === 'queue' || normalized === 'system') {
        return normalized;
      }
    }
    return fallback;
  };

  const formState: FormState = {
    // Tab 1: General — IVR pattern: name=display, context=slug
    queueName: queue.name ?? '',
    queueSlug: queue.context ?? queue.name ?? '',
    extension: (queue.extension ?? '') || getStringSetting(settings, 'extension'),
    strategy: queue.strategy ?? 'longest-idle-agent',
    recordCalls: Boolean(queue.recordCalls),
    maxWaitTime: String(queue.maxWaitTime ?? 0),
    maxWaitTimeWithNoAgent: String(getNumberSetting(settings, 'max_wait_time_with_no_agent', 300)),
    maxWaitTimeWithNoAgentTimeReached: getStringSetting(settings, 'max_wait_time_with_no_agent_time_reached'),
    enabled: Boolean(queue.enabled),
    queueProfileId: queue.queueProfileId ? String(queue.queueProfileId) : '',
    callbackEnabled: Boolean(queue.callbackProfileId),
    callbackProfileId: queue.callbackProfileId ? String(queue.callbackProfileId) : '',
    vipListIds: queue.vipListIds ?? [],
    wrapUpTime: String(getNumberSetting(settings, 'wrap_up_time', 10)),
    queuePriority: String(getNumberSetting(settings, 'cc_priority', 0)),
    afterAgentHangupDestinationModule: parseDestination(getStringSetting(settings, 'execute_on_hangup')).module,
    afterAgentHangupDestinationDestination: parseDestination(getStringSetting(settings, 'execute_on_hangup')).destination,
    timeoutDestinationModule: parseDestination(getStringSetting(settings, 'timeout_destination')).module,
    timeoutDestinationDestination: parseDestination(getStringSetting(settings, 'timeout_destination')).destination,
    serviceLevelSeconds: String(getNumberSetting(settings, 'service_level_seconds', 0)),
    announceHoldTime: getBooleanSetting(settings, 'announce_hold_time', false),
    announcePosition: getBooleanSetting(settings, 'announce_position', false),
    
    // Tab 2: Announcements
    greeting: (queue.greeting ?? '') || getStringSetting(settings, 'greeting'),
    welcomeSound: getStringSetting(settings, 'welcome_sound'),
    exitSound: getStringSetting(settings, 'exit_sound'),
    language: (queue.language ?? 'en-us') || getStringSetting(settings, 'language'),
    musicOnHold: queue.musicOnHold ?? 'local_stream://default',
    agentConnectSound: getStringSetting(settings, 'agent_connect_sound'),
    announceSound: getStringSetting(settings, 'announce_sound'),
    announceFrequency: String(getNumberSetting(settings, 'announce_frequency', 0)),
    announceEstimatedHoldTime: getBooleanSetting(settings, 'announce_estimated_holdtime', false),
    
    // Tab 3: Advanced
    timeBaseScore: asTimeBaseScore(settings.time_base_score, 'queue'),
    timeBaseScoreSeconds: String(getNumberSetting(settings, 'time_base_score_seconds', 0)),
    discardAbandonedAfter: String(getNumberSetting(settings, 'discard_abandoned_after', 60)),
    abandonedResumeAllowed: getBooleanSetting(settings, 'abandoned_resume_allowed', false),
    callerExitKey: getStringSetting(settings, 'caller_exit_key'),
    maxWaitAgents: String(getNumberSetting(settings, 'max_wait_agents', 0)),
    maxNoAnswer: String(getNumberSetting(settings, 'max_no_answer', 3)),
    queueMaxSize: String(getNumberSetting(settings, 'cc_max_wait_size', 0)),
    agentNoAnswerDelay: String(getNumberSetting(settings, 'agent_no_answer_delay_time', 0)),
    
    // Tab 4: Caller Experience
    callerIdNamePrefix: getStringSetting(settings, 'caller_id_name_prefix'),
    callerIdNumberPrefix: getStringSetting(settings, 'caller_id_number_prefix'),
    
    // Tab 5: Tier Rules
    tierRulesApply: getBooleanSetting(settings, 'tier_rules_apply', false),
    tierRuleWait: String(getNumberSetting(settings, 'tier_rule_wait', 0)),
    tierRuleMultiplyLevel: getBooleanSetting(settings, 'tier_rule_multiply_level', false),
    tierRuleNoAgentNoWait: getBooleanSetting(settings, 'tier_rule_no_agent_no_wait', false),
    
    createdAt: queue.createdAt ?? null,
    updatedAt: queue.updatedAt ?? null
  };

  const tiers: TierRow[] = Array.isArray(queue.tiers)
    ? queue.tiers.map((tier: TierResponse) => ({
        id: tier.id,
        agentId: String(tier.agentId),
        level: String(tier.level ?? 1),
        position: String(tier.position ?? 1)
      }))
    : [];

  return { formState, tiers, extraSettings };
}

